/**
 * SPEC-staging-hardening §5.10 — pure helpers for the Admin "Live ingest" card.
 */
import { effectiveLiveIngestMode, type LiveIngestMode } from "@shared/live-ingest";

const MINUTE_MS = 60 * 1000;

/** "1 h 12 m left", "3 m left", "expired". */
export function remainingLabel(fullUntil: string, nowMs: number): string {
  const ms = Date.parse(fullUntil) - nowMs;
  if (!Number.isFinite(ms) || ms <= 0) return "expired";
  const totalMinutes = Math.max(1, Math.floor(ms / MINUTE_MS));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h} h ${m} m left` : `${m} m left`;
}

/** ISO instant `hours` from now. */
export function fullUntilFromDuration(hours: number, nowMs: number): string {
  return new Date(nowMs + hours * 60 * MINUTE_MS).toISOString();
}

/** The status line under the segmented control. Expiry is re-derived from nowMs. */
export function describeLiveIngest(
  s: { mode: LiveIngestMode; fullUntil: string | null; effectiveMode: LiveIngestMode },
  nowMs: number,
): string {
  if (s.mode === "off") return "Off — webhooks are acknowledged and dropped.";
  if (s.mode === "ingest") return "Ingest — deliveries are recorded; the model does not run automatically.";
  if (effectiveLiveIngestMode({ mode: "full", fullUntil: s.fullUntil }, nowMs) !== "full" || !s.fullUntil) {
    return "Full window expired — running as Ingest.";
  }
  const time = new Date(s.fullUntil).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `Full until ${time} (${remainingLabel(s.fullUntil, nowMs)}) — full pipeline running.`;
}
